import React, { useMemo } from 'react';
import ProgressCircles from './ProgressCircles';
import FireworksDisplay from './FireworksDisplay';
import { useTheme } from './ThemeContext';

const TimingVisualization = ({ timeArray, selectedTimeHorizon, currentScore }) => {
  const { theme, isAscii: isAnyAscii } = useTheme();
  const isDark = theme === 'dark';
  const isAscii = isAnyAscii && isDark;
  const isAsciiLight = isAnyAscii && !isDark;

  // Resolved tokens for ascii variants
  const tv = isAscii ? {
    cardBg: '#000000', cardColor: '#33ff33', cardBorder: '2px solid #33ff33',
    metricBoxShadow: 'none', clicksBg: 'rgba(51,255,51,0.07)', clicksBorder: '#33ff33',
    clicksTitle: '#33ff33', maeBg: 'rgba(51,255,51,0.05)', maeBorder: 'rgba(51,255,51,0.5)',
    maeTitle: '#33ff33', avgBg: '#000', avgBorder: 'rgba(51,255,51,0.2)',
    avgTitle: 'rgba(51,255,51,0.6)', metricValue: '#33ff33', analysisBg: '#000',
    analysisBorder: 'rgba(51,255,51,0.25)', analysisColor: '#33ff33',
    svgLabel: '#33ff33', svgLine: 'rgba(51,255,51,0.2)', font: '"Courier New",Courier,monospace',
  } : isAsciiLight ? {
    cardBg: '#f2f2ee', cardColor: '#1a1a1a', cardBorder: '2px solid #444',
    metricBoxShadow: 'none', clicksBg: 'rgba(0,0,0,0.06)', clicksBorder: '#444',
    clicksTitle: '#1a1a1a', maeBg: 'rgba(0,0,0,0.04)', maeBorder: '#888',
    maeTitle: '#1a1a1a', avgBg: '#e8e8e4', avgBorder: '#ccc',
    avgTitle: '#555', metricValue: '#1a1a1a', analysisBg: '#e8e8e4',
    analysisBorder: '#ccc', analysisColor: '#1a1a1a',
    svgLabel: '#333', svgLine: '#ccc', font: '"Courier New",Courier,monospace',
  } : isDark ? {
    cardBg: 'rgba(8,12,26,0.95)', cardColor: '#ddeeff', cardBorder: 'none',
    metricBoxShadow: 'none', clicksBg: 'rgba(120,40,200,0.15)', clicksBorder: 'rgba(157,78,221,0.4)',
    clicksTitle: '#c8a9fa', maeBg: 'rgba(0,100,200,0.12)', maeBorder: 'rgba(0,180,255,0.3)',
    maeTitle: '#00d4ff', avgBg: 'rgba(10,20,45,0.6)', avgBorder: 'rgba(0,245,255,0.12)',
    avgTitle: '#8ab4cc', metricValue: '#ddeeff', analysisBg: 'rgba(4,7,18,0.7)',
    analysisBorder: 'rgba(0,245,255,0.12)', analysisColor: '#ddeeff',
    svgLabel: '#8ab4cc', svgLine: 'rgba(0,245,255,0.18)', font: 'inherit',
  } : {
    cardBg: 'white', cardColor: 'inherit', cardBorder: 'none',
    metricBoxShadow: '0 2px 4px rgba(0,0,0,0.05)', clicksBg: '#F3E8FF', clicksBorder: '#C084FC',
    clicksTitle: '#7E22CE', maeBg: '#E3F2FD', maeBorder: '#90CAF9',
    maeTitle: '#1976D2', avgBg: '#F5F5F5', avgBorder: '#E0E0E0',
    avgTitle: '#616161', metricValue: '#333', analysisBg: 'white',
    analysisBorder: '#E0E0E0', analysisColor: 'inherit',
    svgLabel: '#333', svgLine: '#ddd', font: 'inherit',
  };

  const { clickPairs, meanAbsoluteError, averageDifference } = useMemo(() => {
    if (timeArray.length <= 1) return { clickPairs: [], meanAbsoluteError: 0, averageDifference: 0 };

    const pairs = timeArray.slice(1).map((time, i) => {
      const interval = time - timeArray[i];
      const deviation = interval - 1000;
      return {
        interval,
        deviation,
        absoluteError: Math.abs(deviation),
        isTooEarly: deviation < 0,
        isTooLate: deviation > 0,
        needsFeedback: Math.abs(deviation) > 50
      };
    });

    const mae = pairs.reduce((sum, pair) => sum + pair.absoluteError, 0) / pairs.length;
    const avgDiff = pairs.reduce((sum, pair) => sum + pair.deviation, 0) / pairs.length;

    return { clickPairs: pairs, meanAbsoluteError: mae, averageDifference: avgDiff };
  }, [timeArray]);

  const getMaeMessage = (mae) => {
    if (mae <= 25) return { text: "This is awesome!!!", color: "#FFD700" };
    if (mae <= 85) return { text: "This is great!!", color: "#4CAF50" };
    if (mae <= 150) return { text: "This is good!", color: "#FF9800" };
    return { text: "You can do better!", color: "#F44336" };
  };

  const getFeedbackMessage = (pair) => {
    if (!pair.needsFeedback) return "Great!";
    return pair.isTooEarly ? "TOO EARLY" : "TOO LATE";
  };

  const maeMessage = getMaeMessage(meanAbsoluteError);

  // Adjust base dimensions based on screen size
  const baseWidth = 1000; // Reduced from 1400
  const baseHeight = (clickPairs.length * 120) + 300; // Reduced spacing between clicks
  const aspectRatio = baseHeight / baseWidth;

  return (
    <div className="visualization-card">
      {<FireworksDisplay isActive={meanAbsoluteError <= 25} />}
      <div className="visualization-content">
        {currentScore?.clickCount === undefined && (
          <div className="w-full mb-4">
            <ProgressCircles
              clickCount={timeArray.length}
              timeArray={timeArray}
              selectedTimeHorizon={selectedTimeHorizon}
              isSoloMode={true}
              isInModal={true}
            />
          </div>
        )}

        <div className="metrics-container">
          {currentScore?.clickCount !== undefined && (
            <div className="metric-box clicks-box">
              <div className="metric-title">CLICKS ACHIEVED</div>
              <div className="metric-value">{currentScore.clickCount}</div>
            </div>
          )}
          <div className="metric-box mae-box">
            <div className="metric-title">MEAN ABSOLUTE ERROR</div>
            <div className="metric-value">{meanAbsoluteError.toFixed(1)} ms</div>
            <div className="metric-message" style={{ color: maeMessage.color }}>
              {maeMessage.text}
            </div>
          </div>
          <div className="metric-box avgdiff-box">
            <div className="metric-title">AVERAGE DIFFERENCE</div>
            <div className="metric-value">{averageDifference.toFixed(1)} ms</div>
          </div>
        </div>

        <div className="analysis-box">
          <div style={{ marginBottom: '4px' }}>
            <div style={{
              textAlign: 'center',
              fontSize: '1.25rem',
              fontWeight: '600',
              marginBottom: '4px'
            }}>
              Click Timing Analysis
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap',
              padding: '0 4px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: "#2196f3" }}></div>
                <span style={{ fontSize: '0.875rem' }}>Previous Click</span>
              </div>
              <span style={{ fontSize: '0.875rem' }}>•</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: "#43a047" }}></div>
                <span style={{ fontSize: '0.875rem' }}>Perfect Timing (1.000s)</span>
              </div>
              <span style={{ fontSize: '0.875rem' }}>•</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: "#2196f3" }}></div>
                <span style={{ fontSize: '0.875rem' }}>Your Click</span>
              </div>
            </div>
          </div>

          <div className="visualization-container" style={{ marginTop: '0' }}>
            <div className="svg-container">
              <svg
                className="timing-svg"
                viewBox={`0 0 ${baseWidth} ${baseHeight}`}
                preserveAspectRatio="xMidYMid meet"
              >
                {clickPairs.map((pair, index) => {
                  const y = 100 + (index * 120); // Start visualization higher up
                  const baseX = 200; // Reduced left margin
                  const targetX = 800; // Reduced right position
                  const baseDistance = 600; // Reduced total width
                  const deviationScale = baseDistance / 1000;
                  const feedbackColor = pair.needsFeedback ?
                    (pair.absoluteError < 200 ? "#ff9800" : "#f44336") :
                    "#43a047";

                  return (
                    <g key={index}>
                      <text x="50" y={y + 5} className="click-label" fill={tv.svgLabel} fontSize="24" fontWeight="bold">
                        Click {index + 1}
                      </text>

                      <line x1={baseX} y1={y} x2={targetX} y2={y} stroke={tv.svgLine} strokeWidth="3" />
                      <circle cx={baseX} cy={y} r="12" fill="#2196f3" />
                      <circle cx={targetX} cy={y} r="12" fill="#43a047" />
                      <circle
                        cx={targetX + (pair.deviation * deviationScale)}
                        cy={y}
                        r="12"
                        fill="#2196f3"
                      />

                      <text
                        x={targetX + (pair.deviation * deviationScale)}
                        y={y - 20}
                        textAnchor="middle"
                        className="feedback-text"
                        fill={feedbackColor}
                        fontSize="24"
                      >
                        {getFeedbackMessage(pair)}
                      </text>

                      <line
                        x1={targetX}
                        y1={y + 20}
                        x2={targetX + (pair.deviation * deviationScale)}
                        y2={y + 20}
                        stroke={feedbackColor}
                        strokeWidth="4"
                      />

                      <text
                        x={targetX + (pair.deviation * deviationScale / 2)}
                        y={y + 40}
                        textAnchor="middle"
                        className="time-text"
                        fill={feedbackColor}
                        fontSize="20"
                      >
                        {(Math.abs(pair.deviation) / 1000).toFixed(3)}s
                      </text>
                    </g>
                  );
                })}

                {clickPairs.length > 0 && (
                  <line
                    x1={800}
                    y1={160}
                    x2={800}
                    y2={160 + ((clickPairs.length - 1) * 120)}
                    stroke="#43a047"
                    strokeWidth="3"
                    strokeDasharray="6"
                  />
                )}
              </svg>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .visualization-card {
          background: ${tv.cardBg};
          border-radius: ${isAnyAscii ? '0' : '12px'};
          border: ${tv.cardBorder};
          box-shadow: ${isAnyAscii ? 'none' : isDark ? '0 0 0 1px rgba(0,245,255,0.08), 0 4px 24px rgba(0,0,0,0.8)' : '0 4px 6px rgba(0,0,0,0.1)'};
          margin: 1rem auto;
          width: 100%;
          max-width: 1200px;
          color: ${tv.cardColor};
          font-family: ${tv.font};
        }

        .visualization-content {
          padding: 1rem;
        }

        .visualization-title {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 0.25rem;
          text-align: center;
          color: ${tv.cardColor};
        }

        .metrics-container {
          display: flex;
          justify-content: center;
          gap: 1rem;
          margin: 1rem 0;
          flex-wrap: wrap;
        }

        .metric-box {
          padding: 1rem;
          border-radius: ${isAnyAscii ? '0' : '8px'};
          text-align: center;
          min-width: 200px;
          flex: 1;
          box-shadow: ${tv.metricBoxShadow};
        }

        .clicks-box {
          background-color: ${tv.clicksBg};
          border: 1px solid ${tv.clicksBorder};
          font-weight: bold;
        }

        .clicks-box .metric-title {
          color: ${tv.clicksTitle};
        }

        .mae-box {
          background-color: ${tv.maeBg};
          border: 1px solid ${tv.maeBorder};
          font-weight: bold;
        }

        .avgdiff-box {
          background-color: ${tv.avgBg};
          border: 1px solid ${tv.avgBorder};
        }

        .metric-title {
          font-size: 0.875rem;
          font-weight: 700;
          color: ${tv.maeTitle};
          margin-bottom: 0.75rem;
        }

        .avgdiff-box .metric-title {
          color: ${tv.avgTitle};
        }

        .metric-value {
          font-size: 1.75rem;
          font-weight: 600;
          color: ${tv.metricValue};
          margin-bottom: 0.5rem;
        }

        .analysis-box {
          border: 1px solid ${tv.analysisBorder};
          border-radius: ${isAnyAscii ? '0' : '8px'};
          padding: 1rem;
          margin-top: 1rem;
          background-color: ${tv.analysisBg};
          color: ${tv.analysisColor};
        }

        .legend-container {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 0.25rem;
          padding: 0.25rem;
          flex-wrap: wrap;
          margin-bottom: 0.25rem;
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.75rem;
        }

        .legend-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
        }

        .visualization-container {
          width: 100%;
          overflow-x: auto;
          margin-top: 0;
        }

        .svg-container {
          display: inline-block;
          position: relative;
          width: 100%;
          padding-bottom: ${aspectRatio * 100}%;
          vertical-align: top;
          overflow: hidden;
        }

        .timing-svg {
          display: inline-block;
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
        }

        @media (max-width: 768px) {
          .visualization-content {
            padding: 0.5rem;
          }

          .metric-box {
            padding: 0.75rem;
            min-width: 150px;
          }

          .metric-value {
            font-size: 1.25rem;
          }

          .legend-container {
            font-size: 0.625rem;
            gap: 0.25rem;
          }

          .visualization-container {
            margin-top: 0;
          }

          .svg-container {
            padding-bottom: ${(aspectRatio * 120)}%;
          }
        }

        @media (max-width: 480px) {
          .metrics-container {
            flex-direction: column;
          }

          .metric-box {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default TimingVisualization;